<template>
  <!-- A wide table scrolls sideways instead of stretching the page. -->
  <el-scrollbar
    v-if="safeHtml"
    v-loading="loading"
  >
    <!-- eslint-disable vue/no-v-html -->
    <!-- Listens for the rows of the groups it folds, made focusable in foldGroups. -->
    <div
      ref="content"
      class="progress-domain-meta"
      @click="toggleGroup"
      @keydown="onKeydown"
      v-html="safeHtml"
    />
    <!-- eslint-enable vue/no-v-html -->
  </el-scrollbar>
</template>

<script>
import htmlSanitizer from '@/services/htmlSanitizer';

// The first row of a progress-group body heads the rows after it, folded under it (SMP-1704).
const GROUP_HEAD = 'tbody.progress-group > tr:first-child';

/**
 * The domainMeta snapshot of a step: HTML produced by the external publisher, rendered only
 * after sanitizing it. Renders nothing when no markup survives.
 */
export default {
  name: 'ProgressDomainMeta',
  props: {
    contentHtml: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    safeHtml() {
      return htmlSanitizer.sanitize(this.contentHtml);
    },
  },
  watch: {
    // v-html replaces the rows only on a new snapshot: an open group stays open meanwhile.
    safeHtml() {
      this.$nextTick(this.foldGroups);
    },
  },
  mounted() {
    this.foldGroups();
  },
  methods: {
    // Every group folded, its head focusable and saying so.
    foldGroups() {
      if (!this.$refs.content) return;
      this.$refs.content.querySelectorAll(GROUP_HEAD).forEach((head) => {
        if (!head.nextElementSibling) return;
        head.setAttribute('tabindex', '0');
        this.setExpanded(head, false);
      });
    },
    toggleGroup(event) {
      const head = event.target.closest(GROUP_HEAD);
      if (!head?.hasAttribute('aria-expanded')) return;
      this.setExpanded(head, head.getAttribute('aria-expanded') !== 'true');
    },
    // A row given the focus reacts to the keys of a button.
    onKeydown(event) {
      if (!['Enter', ' '].includes(event.key) || !event.target.matches(GROUP_HEAD)) return;
      // Space would scroll the page.
      event.preventDefault();
      this.toggleGroup(event);
    },
    setExpanded(head, expanded) {
      head.setAttribute('aria-expanded', String(expanded));
      for (let row = head.nextElementSibling; row; row = row.nextElementSibling) {
        row.hidden = !expanded;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// The publisher's markup carries no class or style: its table is given el-table's look here,
// from the same theme variables.
.progress-domain-meta ::v-deep {
  font-size: $--font-size-base;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  // Facts about the step, one per line: the term quiet, its value in full colour.
  dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.5rem 1rem;
    margin: 0 0 1.5rem;
  }

  dt {
    color: $--color-text-secondary;
  }

  dd {
    margin: 0;
    color: $--color-text-primary;
  }

  // Heads the group of rows it belongs to: closer to its table than to the one above.
  caption {
    padding: 0 0.625rem 0.5rem;
    color: $--color-text-secondary;
    font-size: $--font-size-small;
    font-weight: 500;
    text-align: left;
  }

  table + table {
    margin-top: 1.5rem;
  }

  // Fixed layout: column widths depend on the columns, not on the content, so they stay put when
  // another step is selected. Below the minimum width the table scrolls instead.
  table {
    width: 100%;
    min-width: 40rem;
    table-layout: fixed;
    border-collapse: collapse;
    color: $--table-font-color;
  }

  th,
  td {
    padding: 0.75rem 0.625rem;
    border-bottom: $--table-border;
    text-align: left;
    overflow-wrap: break-word;
  }

  // The first column names what the row is about, the longest text of the row.
  th:first-child {
    width: 40%;
  }

  th {
    color: $--table-header-font-color;
    font-weight: 500;
    background-color: $--table-header-background-color;
  }

  tbody tr:hover {
    background-color: $--table-row-hover-background-color;
  }

  // The head of a class, which unfolds its children: an arrow before its name, as el-table's
  // expandable rows have, turned down while open.
  [aria-expanded] {
    cursor: pointer;

    > td:first-child::before {
      content: '';
      display: inline-block;
      margin-right: 0.5rem;
      border-top: 0.3rem solid transparent;
      border-bottom: 0.3rem solid transparent;
      border-left: 0.35rem solid $--color-text-secondary;
      transition: transform 0.2s;
    }
  }

  [aria-expanded='true'] > td:first-child::before {
    transform: rotate(90deg);
  }

  // Under their class: indented past its arrow.
  [aria-expanded] ~ tr > td:first-child {
    padding-left: 1.75rem;
  }
}
</style>
